import React, { Component } from "react";
import { connect } from "react-redux";
import CheckoutMinimalLayout from "../../components/CheckoutMinimalLayout";
import CheckoutStepCard from "../../components/CheckoutStepCard";
import OrderSummaryWidget from "../../components/OrderSummaryWidget";
import AddressList from "../../components/AddressStep/AddressList";
import RegisteredAddressForm from "../../components/AddressStep/RegisteredAddressForm";
import GuestAddressForm from "../../components/AddressStep/GuestAddressForm";
import InvoiceAddressSelector from "../../components/InvoiceAddressSelector";
import PaymentStep from "../../components/PaymentStep";
import {
  createNewOrderForCustomer,
  createNewOrderForGuest,
  initiatePaytrPayment,
} from "../../store/actions/order";
import {
  customerGetAddress,
  addNewAddress,
  updateAddress,
} from "../../store/actions/customer";
import calculatePrice from "../../helpers/calculatePrice";
import activeDiscount from "../../helpers/activeDiscount";
import { syncCartToBackend } from "../../utils/cartSync";

/**
 * Unified Checkout Component
 *
 * Tek bir ödeme sayfası - hem kayıtlı hem misafir kullanıcılar için
 *
 * Features:
 * - Otomatik user type detection (giriş yapmış / misafir)
 * - Conditional rendering (AddressList vs GuestAddressForm)
 * - Unified payment flow (Havale/EFT ve Online Ödeme)
 * - Code reusability (duplicate code yok)
 */
class UnifiedCheckout extends Component {
  constructor(props) {
    super(props);

    // User type detection
    const isGuest = !props.customer.customer;

    this.state = {
      // User type
      isGuest: isGuest,

      // Common states
      currentStep: 1, // 1: Adres, 2: Kargo, 3: Ödeme
      completedSteps: [],
      isProcessing: false,
      paymentError: null, // Ödeme hatası mesajı
      errors: [], // Sipariş hataları listesi

      // Address management (unified)
      selectedAddressId: null, // Kayıtlı kullanıcı için
      savedAddressData: null, // Misafir için veya yeni adres cache

      // Invoice (unified)
      savedInvoiceData: null,

      // Kayıtlı kullanıcı için ek state'ler
      isSavingAddress: false,
      editingAddressId: null,
      deliveryFormData: null,
      invoiceSameAsDelivery: true,
      invoiceAddressState: {
        selectedAddressId: null,
        isAddingNew: false,
        editingAddressId: null,
        formData: null,
      },
      newAddressData: null,
    };

    // Refs
    this.addressFormRef = React.createRef(); // Misafir için
    this.deliveryAddressFormRef = React.createRef(); // Kayıtlı kullanıcı için
    this.invoiceAddressSelectorRef = React.createRef(); // Kayıtlı kullanıcı için
    this.paymentStepRef = React.createRef();
  }

  /**
   * Helper: Save checkout form data to localStorage with 30-day expiry
   */
  saveCheckoutFormData(data) {
    var checkoutData = {
      data: data,
      timestamp: Date.now(),
    };
    localStorage.setItem("checkout_form_data", JSON.stringify(checkoutData));
  }

  /**
   * Helper: Get checkout form data from localStorage with expiry check
   * Returns null if expired or not found
   */
  getCheckoutFormData() {
    try {
      var stored = localStorage.getItem("checkout_form_data");
      if (!stored) {
        return null;
      }

      var parsed = JSON.parse(stored);

      // Backward compatibility: if no timestamp, treat as old format
      if (!parsed.timestamp) {
        // Old format - return as is (will expire on next save)
        console.warn("[UnifiedCheckout] Old checkout_form_data format detected");
        return parsed;
      }

      // New format: check expiry (30 days)
      var thirtyDays = 30 * 24 * 60 * 60 * 1000;
      if (Date.now() - parsed.timestamp < thirtyDays) {
        return parsed.data;
      } else {
        // Expired - remove it
        console.log("[UnifiedCheckout] checkout_form_data expired, removing");
        localStorage.removeItem("checkout_form_data");
        return null;
      }
    } catch (e) {
      console.error("[UnifiedCheckout] localStorage parse error:", e);
      localStorage.removeItem("checkout_form_data");
      return null;
    }
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    // Ödeme sayfası URL'ini localStorage'a kaydet
    localStorage.setItem("checkout_url", "/odeme");

    // Ödeme hatası kontrolü (PayTR'den redirect ile gelindi mi?)
    const urlParams = new URLSearchParams(this.props.location.search);
    const errorParam = urlParams.get("error");
    if (errorParam) {
      // localStorage'tan form verilerini yükle
      const savedFormData = this.getCheckoutFormData();
      if (savedFormData) {
        this.setState({
          paymentError: decodeURIComponent(errorParam),
          currentStep: savedFormData.currentStep || 3,
          completedSteps: savedFormData.completedSteps || [1, 2],
          isGuest: savedFormData.isGuest,
          savedAddressData: savedFormData.savedAddressData || null,
          selectedAddressId: savedFormData.selectedAddressId || null,
          newAddressData: savedFormData.newAddressData || null,
          savedInvoiceData: savedFormData.savedInvoiceData || null,
        });
      } else {
        this.setState({
          paymentError: decodeURIComponent(errorParam),
          currentStep: 3,
          completedSteps: [1, 2],
        });
      }

      // URL'den error parametresini temizle
      window.history.replaceState({}, document.title, "/odeme");
    }

    // Sepet boşsa sepete yönlendir
    if (!this.props.cart.items || this.props.cart.items.length === 0) {
      this.props.history.push("/sepet");
      return;
    }

    // Kayıtlı kullanıcı ise adresleri yükle
    if (!this.state.isGuest) {
      this.props.customerGetAddress().then(
        function () {
          const addresses = this.props.customer.addresses;
          if (
            addresses &&
            addresses.length > 0 &&
            !this.state.selectedAddressId
          ) {
            // İlk adresi varsayılan olarak seç (aktif adres özelliği kaldırıldı)
            this.setState({ selectedAddressId: addresses[0].id });
          } else if (!addresses || addresses.length === 0) {
            // Adres yoksa otomatik olarak yeni adres ekleme moduna geç
            this.setState({ selectedAddressId: "new" });
          }
        }.bind(this)
      );

      // Default invoice data
      if (!this.state.savedInvoiceData) {
        this.setState({
          savedInvoiceData: { sameAsDelivery: true },
        });
      }
    }
  }

  componentDidUpdate(prevProps) {
    // Sipariş oluşturma hatası olursa loading'i kapat ve kullanıcıya göster
    if (
      prevProps.order.pending &&
      !this.props.order.pending &&
      this.props.order.error
    ) {
      this.setState({ isProcessing: false });

      var errors = [];

      // Eğer spesifik hata mesajı varsa onu kullan
      if (this.props.order.error && this.props.order.error.length > 0) {
        errors = this.props.order.error.map(function (err) {
          return err.message || err.toString();
        });
      } else {
        errors = [
          "Sipariş oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.",
        ];
      }

      console.error(
        "[UnifiedCheckout] Sipariş hatası:",
        this.props.order.error
      );

      // Hataları state'e ekle (render'da gösterilecek)
      this.setState({
        errors: errors,
        isProcessing: false,
      });

      // Sayfayı en üste scroll et ki kullanıcı hatayı görsün
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    // Sipariş başarılı olduğunda errors'ı temizle ve isProcessing'i false yap
    if (
      prevProps.order.pending &&
      !this.props.order.pending &&
      !this.props.order.error &&
      this.props.order.item
    ) {
      this.setState({
        isProcessing: false,
        errors: [],
      });
    }
  }

  // ==================== PRICE CALCULATION ====================

  calculateCustomerDiscount = () => {
    if (this.state.isGuest) return 0;

    const { customer } = this.props.customer;
    const { items } = this.props.cart;

    if (
      !customer ||
      !customer.discount_enabled ||
      !items ||
      items.length === 0
    ) {
      return 0;
    }

    const cartSubtotal = items.reduce(function (total, item) {
      const price = item.sale_price || item.price;
      const discount = activeDiscount(item.categories);
      let itemPrice = price * item.quantity;

      if (discount) {
        if (discount.type === "percent") {
          itemPrice = itemPrice * (1 - discount.discount / 100);
        } else {
          itemPrice = itemPrice - discount.discount * item.quantity;
        }
      }

      return total + itemPrice;
    }, 0);

    if (cartSubtotal < customer.min_cart_amount) {
      return 0;
    }

    if (customer.discount_type === "percentage") {
      return cartSubtotal * (customer.discount_value / 100);
    } else if (customer.discount_type === "fixed") {
      return customer.discount_value;
    }

    return 0;
  };

  calculatePrices = () => {
    const { cart, setting } = this.props;

    let cartTotalPrice = 0;
    if (cart.items && cart.items.length > 0) {
      cartTotalPrice = parseFloat(
        cart.items
          .map(function (v) {
            const quantity = parseInt(v.quantity) || 0;
            const price = parseFloat(v.sale_price || v.price) || 0;
            return quantity * price;
          })
          .reduce(function (a, b) {
            return a + b;
          })
          .toFixed(2)
      );
    }

    let cartTotalDiscountPrice = 0;
    if (cart.items && cart.items.length > 0) {
      cartTotalDiscountPrice = parseFloat(
        cart.items
          .map(function (product) {
            return parseFloat(
              calculatePrice(product, activeDiscount(product.categories))
            );
          })
          .reduce(function (a, b) {
            return a + b;
          })
          .toFixed(2)
      );
    }

    const customerDiscount = this.calculateCustomerDiscount();
    const finalCartPrice = cartTotalDiscountPrice - customerDiscount;

    const freeShippingThreshold =
      setting && setting.setting && setting.setting.free_shipping
        ? setting.setting.free_shipping
        : 0;

    const shippingCost =
      cart.items &&
      cart.items.length > 0 &&
      finalCartPrice > 0 &&
      finalCartPrice < freeShippingThreshold
        ? setting && setting.setting && setting.setting.shipping_cost
          ? setting.setting.shipping_cost
          : 0
        : 0;

    const totalPrice = finalCartPrice + shippingCost;

    return {
      cartTotalPrice,
      cartTotalDiscountPrice,
      customerDiscount,
      shippingCost,
      totalPrice,
      isFreeShipping: shippingCost === 0,
    };
  };

  // ==================== ADDRESS HANDLERS (REGISTERED) ====================

  handleAddressSelect = (address) => {
    this.setState({
      selectedAddressId: address.id,
      newAddressData: null,
    });
  };

  handleNewAddress = () => {
    this.setState({
      selectedAddressId: "new",
      editingAddressId: null,
    });
  };

  handleEditAddress = (address) => {
    this.setState({
      selectedAddressId: address.id,
      editingAddressId: address.id,
    });
  };

  handleCancelEdit = () => {
    this.setState({
      editingAddressId: null,
    });
  };

  handleDeliveryFormChange = (formData) => {
    this.setState({
      deliveryFormData: formData,
    });
  };

  handleSaveEdit = (addressId) => {
    if (!this.deliveryAddressFormRef.current) {
      alert("Form yüklenemedi. Lütfen sayfayı yenileyin.");
      return;
    }

    const addressData = this.deliveryAddressFormRef.current.getData();
    if (!addressData) {
      return;
    }

    this.setState({ isSavingAddress: true });

    const updatedAddress = {
      id: addressId,
      address_name: addressData.address_name,
      first_name: addressData.first_name,
      last_name: addressData.last_name,
      city: addressData.city,
      town: addressData.town,
      phone: addressData.phone,
      address: addressData.address,
      active: false,
      invoice_type: addressData.invoice_type || "individual",
      company_name: addressData.company_name || "",
      tax_number: addressData.tax_number || "",
      tax_office: addressData.tax_office || "",
    };

    this.props
      .updateAddress(updatedAddress)
      .then(
        function () {
          return this.props.customerGetAddress();
        }.bind(this)
      )
      .then(
        function () {
          this.setState({
            editingAddressId: null,
            isSavingAddress: false,
          });
        }.bind(this)
      )
      .catch(
        function (error) {
          console.error("Adres güncelleme hatası:", error);
          alert("Adres güncellenirken bir hata oluştu.");
          this.setState({ isSavingAddress: false });
        }.bind(this)
      );
  };

  getEditingAddressData = () => {
    const { editingAddressId, deliveryFormData } = this.state;
    const { addresses } = this.props.customer;

    if (deliveryFormData && editingAddressId) {
      return deliveryFormData;
    }

    if (!editingAddressId || !addresses) return null;

    const address = addresses.find(function (addr) {
      return addr.id === editingAddressId;
    });

    if (!address) return null;

    return {
      first_name: address.first_name || "",
      last_name: address.last_name || "",
      city: address.city || "",
      town: address.town || "",
      phone: address.phone || "",
      address: address.address || "",
      address_name: address.address_name || "",
      saveAddress: true,
      invoice_type: address.invoice_type || "individual",
      company_name: address.company_name || "",
      tax_number: address.tax_number || "",
      tax_office: address.tax_office || "",
    };
  };

  getNewAddressData = () => {
    const { deliveryFormData } = this.state;
    return deliveryFormData || null;
  };

  // Invoice handlers
  handleInvoiceSameAsDeliveryChange = (value) => {
    this.setState({
      invoiceSameAsDelivery: value,
    });
  };

  handleInvoiceAddressStateChange = (newState) => {
    this.setState({
      invoiceAddressState: {
        ...this.state.invoiceAddressState,
        ...newState,
      },
    });
  };

  handleSaveInvoiceAddress = (addressId, addressData) => {
    this.setState({ isSavingAddress: true });

    const addressToSave = {
      address_name: addressData.address_name,
      first_name: addressData.first_name,
      last_name: addressData.last_name,
      city: addressData.city,
      town: addressData.town,
      phone: addressData.phone,
      address: addressData.address,
      active: false,
      invoice_type: addressData.invoice_type || "individual",
      company_name: addressData.company_name || "",
      tax_number: addressData.tax_number || "",
      tax_office: addressData.tax_office || "",
    };

    if (addressId) {
      // Update
      addressToSave.id = addressId;
      this.props
        .updateAddress(addressToSave)
        .then(
          function () {
            return this.props.customerGetAddress();
          }.bind(this)
        )
        .then(
          function () {
            this.setState({ isSavingAddress: false });
            alert("Fatura adresi başarıyla güncellendi.");
          }.bind(this)
        )
        .catch(
          function (error) {
            console.error("Fatura adresi güncelleme hatası:", error);
            alert("Adres güncellenirken bir hata oluştu.");
            this.setState({ isSavingAddress: false });
          }.bind(this)
        );
    } else {
      // Add new
      this.props
        .addNewAddress(addressToSave)
        .then(
          function () {
            return this.props.customerGetAddress();
          }.bind(this)
        )
        .then(
          function () {
            const addresses = this.props.customer.addresses;
            if (addresses && addresses.length > 0) {
              const newlyAddedAddress = addresses.find(function (addr) {
                return (
                  addr.address_name === addressData.address_name &&
                  addr.phone === addressData.phone
                );
              });

              if (newlyAddedAddress) {
                this.setState({
                  isSavingAddress: false,
                  invoiceAddressState: {
                    ...this.state.invoiceAddressState,
                    selectedAddressId: newlyAddedAddress.id,
                    isAddingNew: false,
                    formData: null,
                  },
                });
                alert("Fatura adresi başarıyla kaydedildi.");
              } else {
                this.setState({ isSavingAddress: false });
                alert("Adres kaydedildi.");
              }
            }
          }.bind(this)
        )
        .catch(
          function (error) {
            console.error("Fatura adresi kaydetme hatası:", error);
            alert("Adres kaydedilirken bir hata oluştu.");
            this.setState({ isSavingAddress: false });
          }.bind(this)
        );
    }
  };

  // ==================== STEP NAVIGATION ====================

  handleStepNext = () => {
    const { currentStep, selectedAddressId, isGuest } = this.state;

    // Adım 1: Adres validasyonu
    if (currentStep === 1) {
      if (isGuest) {
        // Misafir: GuestAddressForm validasyonu
        if (!this.addressFormRef.current) {
          alert("Form yüklenemedi. Lütfen sayfayı yenileyin.");
          return;
        }

        const addressData = this.addressFormRef.current.getData();
        if (!addressData) {
          return; // Validasyon hatası form içinde gösterilir
        }

        // Form verilerini localStorage'a kaydet (PayTR fail redirect için)
        this.saveCheckoutFormData({
          isGuest: true,
          savedAddressData: addressData,
          currentStep: 2,
          completedSteps: [1],
        });

        this.setState({
          currentStep: 2,
          completedSteps: [1],
          savedAddressData: addressData,
        });
        window.scrollTo(0, 0);
        return;
      } else {
        // Kayıtlı kullanıcı: Address selection veya new address
        if (selectedAddressId === "new") {
          if (!this.deliveryAddressFormRef.current) {
            alert("Form yüklenemedi. Lütfen sayfayı yenileyin.");
            return;
          }

          const addressData = this.deliveryAddressFormRef.current.getData();
          if (!addressData) {
            return;
          }

          // Eğer kullanıcı adresi kaydetmek istiyorsa
          if (addressData.saveAddress && addressData.address_name) {
            // Önce fatura bilgilerini al
            let invoiceData = null;
            if (this.invoiceAddressSelectorRef.current) {
              invoiceData = this.invoiceAddressSelectorRef.current.getData();

              if (!invoiceData) {
                alert("Lütfen fatura bilgilerini doldurunuz.");
                return;
              }
            }

            const newAddressToSave = {
              address_name: addressData.address_name,
              first_name: addressData.first_name,
              last_name: addressData.last_name,
              city: addressData.city,
              town: addressData.town,
              phone: addressData.phone,
              address: addressData.address,
              active: false,
              invoice_type: addressData.invoice_type || "individual",
              company_name: addressData.company_name || "",
              tax_number: addressData.tax_number || "",
              tax_office: addressData.tax_office || "",
            };

            this.props
              .addNewAddress(newAddressToSave)
              .then(
                function () {
                  return this.props.customerGetAddress();
                }.bind(this)
              )
              .then(
                function () {
                  const addresses = this.props.customer.addresses;
                  if (addresses && addresses.length > 0) {
                    const newlyAddedAddress = addresses.find(function (addr) {
                      return (
                        addr.address_name === addressData.address_name &&
                        addr.phone === addressData.phone
                      );
                    });

                    if (newlyAddedAddress) {
                      // Form verilerini localStorage'a kaydet
                      this.saveCheckoutFormData({
                        isGuest: false,
                        selectedAddressId: newlyAddedAddress.id,
                        savedInvoiceData: invoiceData,
                        currentStep: 2,
                        completedSteps: [1],
                      });

                      this.setState({
                        selectedAddressId: newlyAddedAddress.id,
                        newAddressData: null,
                        deliveryFormData: null,
                        savedInvoiceData: invoiceData,
                        currentStep: 2,
                        completedSteps: [1],
                      });
                    } else {
                      this.saveCheckoutFormData({
                        isGuest: false,
                        selectedAddressId: addresses[addresses.length - 1].id,
                        savedInvoiceData: invoiceData,
                        currentStep: 2,
                        completedSteps: [1],
                      });

                      this.setState({
                        selectedAddressId: addresses[addresses.length - 1].id,
                        newAddressData: null,
                        deliveryFormData: null,
                        savedInvoiceData: invoiceData,
                        currentStep: 2,
                        completedSteps: [1],
                      });
                    }
                  } else {
                    // Form verilerini localStorage'a kaydet
                    this.saveCheckoutFormData({
                      isGuest: false,
                      selectedAddressId: "new",
                      newAddressData: addressData,
                      savedInvoiceData: invoiceData,
                      currentStep: 2,
                      completedSteps: [1],
                    });

                    this.setState({
                      newAddressData: addressData,
                      savedInvoiceData: invoiceData,
                      currentStep: 2,
                      completedSteps: [1],
                    });
                  }
                  window.scrollTo(0, 0);
                }.bind(this)
              )
              .catch(
                function (error) {
                  console.error("Adres kaydetme hatası:", error);
                  alert(
                    "Adres kaydedilirken bir hata oluştu. Ancak sipariş için kullanılacaktır."
                  );
                  this.setState({
                    newAddressData: addressData,
                    savedInvoiceData: invoiceData,
                    currentStep: 2,
                    completedSteps: [1],
                  });
                  window.scrollTo(0, 0);
                }.bind(this)
              );
            return;
          } else {
            // Adres kaydedilmeyecek, sadece bu sipariş için kullanılacak
            // Fatura bilgilerini de al
            if (this.invoiceAddressSelectorRef.current) {
              const invoiceData =
                this.invoiceAddressSelectorRef.current.getData();

              if (!invoiceData) {
                alert("Lütfen fatura bilgilerini doldurunuz.");
                return;
              }

              // Form verilerini localStorage'a kaydet (PayTR fail redirect için)
              this.saveCheckoutFormData({
                isGuest: false,
                selectedAddressId: "new",
                newAddressData: addressData,
                savedInvoiceData: invoiceData,
                currentStep: 2,
                completedSteps: [1],
              });

              this.setState({
                newAddressData: addressData,
                savedInvoiceData: invoiceData,
                currentStep: 2,
                completedSteps: [1],
              });
            } else {
              this.setState({
                newAddressData: addressData,
                currentStep: 2,
                completedSteps: [1],
              });
            }
            window.scrollTo(0, 0);
            return;
          }
        } else if (this.state.editingAddressId) {
          // Düzenleme modu
          if (!this.deliveryAddressFormRef.current) {
            alert("Form yüklenemedi. Lütfen sayfayı yenileyin.");
            return;
          }

          const addressData = this.deliveryAddressFormRef.current.getData();
          if (!addressData) {
            return;
          }

          const updatedAddress = {
            id: this.state.editingAddressId,
            address_name: addressData.address_name,
            first_name: addressData.first_name,
            last_name: addressData.last_name,
            city: addressData.city,
            town: addressData.town,
            phone: addressData.phone,
            address: addressData.address,
            active: false,
            invoice_type: addressData.invoice_type || "individual",
            company_name: addressData.company_name || "",
            tax_number: addressData.tax_number || "",
            tax_office: addressData.tax_office || "",
          };

          this.props
            .updateAddress(updatedAddress)
            .then(
              function () {
                return this.props.customerGetAddress();
              }.bind(this)
            )
            .then(
              function () {
                this.setState({
                  editingAddressId: null,
                  currentStep: 2,
                  completedSteps: [1],
                });
                window.scrollTo(0, 0);
              }.bind(this)
            )
            .catch(function (error) {
              console.error("Adres güncelleme hatası:", error);
              alert("Adres güncellenirken bir hata oluştu.");
            });
          return;
        } else {
          // Kayıtlı adres seçimi
          if (!selectedAddressId) {
            alert("Lütfen bir teslimat adresi seçiniz.");
            return;
          }

          const addresses = this.props.customer.addresses;
          const addressExists =
            addresses &&
            addresses.find(function (addr) {
              return addr.id === selectedAddressId;
            });

          if (!addressExists) {
            alert("Seçili adres bulunamadı. Lütfen tekrar bir adres seçiniz.");
            return;
          }

          // Zorunlu alan kontrolü
          var missingFields = [];
          if (!addressExists.first_name || addressExists.first_name.trim() === "") {
            missingFields.push("Ad");
          }
          if (!addressExists.last_name || addressExists.last_name.trim() === "") {
            missingFields.push("Soyad");
          }
          if (!addressExists.phone || addressExists.phone.trim() === "") {
            missingFields.push("Telefon");
          }
          if (!addressExists.city || addressExists.city.trim() === "") {
            missingFields.push("Şehir");
          }
          if (!addressExists.town || addressExists.town.trim() === "") {
            missingFields.push("İlçe");
          }
          if (!addressExists.address || addressExists.address.trim() === "") {
            missingFields.push("Adres");
          }

          if (missingFields.length > 0) {
            alert(
              "Seçili adreste şu zorunlu alanlar eksik: " +
                missingFields.join(", ") +
                ".\n\nAdres düzenleme ekranı açılıyor. Lütfen eksik bilgileri tamamlayın."
            );

            // Otomatik olarak adresi düzenleme moduna al
            this.setState({
              editingAddressId: selectedAddressId,
            });

            // Sayfayı yukarı kaydır ki kullanıcı formu görsün
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
          }
        }

        // Fatura adresi validasyonu ve kaydetme
        if (this.invoiceAddressSelectorRef.current) {
          const invoiceData = this.invoiceAddressSelectorRef.current.getData();

          if (!invoiceData) {
            alert("Lütfen fatura bilgilerini doldurunuz.");
            return;
          }

          if (!invoiceData.sameAsDelivery) {
            if (
              !invoiceData.selectedAddressId &&
              !invoiceData.isNewAddress &&
              !invoiceData.isEditMode
            ) {
              alert("Lütfen fatura adresi seçiniz veya yeni adres ekleyiniz.");
              return;
            }
          }

          // Form verilerini localStorage'a kaydet (PayTR fail redirect için)
          this.saveCheckoutFormData({
            isGuest: false,
            selectedAddressId: selectedAddressId,
            newAddressData: this.state.newAddressData,
            savedInvoiceData: invoiceData,
            currentStep: 2,
            completedSteps: [1],
          });

          this.setState({
            savedInvoiceData: invoiceData,
            currentStep: 2,
            completedSteps: [1],
          });
        } else {
          // Form verilerini localStorage'a kaydet (PayTR fail redirect için)
          this.saveCheckoutFormData({
            isGuest: false,
            selectedAddressId: selectedAddressId,
            newAddressData: this.state.newAddressData,
            currentStep: 2,
            completedSteps: [1],
          });

          this.setState({
            currentStep: 2,
            completedSteps: [1],
          });
        }

        window.scrollTo(0, 0);
        return;
      }
    }

    // Adım 2: Kargo → Ödeme
    if (currentStep === 2) {
      if (!isGuest && !selectedAddressId) {
        alert("Lütfen adres adımına dönüp bir teslimat adresi seçiniz.");
        this.setState({ currentStep: 1 });
        window.scrollTo(0, 0);
        return;
      }

      // localStorage'daki form verisini güncelle (adım 3'e geçiş için)
      const savedFormData = this.getCheckoutFormData();
      if (savedFormData) {
        savedFormData.currentStep = 3;
        savedFormData.completedSteps = [1, 2];
        this.saveCheckoutFormData(savedFormData);
      }

      this.setState({
        currentStep: 3,
        completedSteps: [1, 2],
      });
      window.scrollTo(0, 0);
      return;
    }
  };

  handleStepBack = () => {
    const { currentStep } = this.state;
    if (currentStep > 1) {
      this.setState({ currentStep: currentStep - 1 });
      window.scrollTo(0, 0);
    }
  };

  // ==================== SUBMIT & PAYMENT ====================

  onSubmit = (e) => {
    e.preventDefault();

    if (this.state.currentStep !== 3) {
      alert("Lütfen tüm adımları tamamlayın.");
      return;
    }

    if (!this.paymentStepRef.current) {
      alert("Ödeme formu yüklenemedi. Lütfen ödeme adımına ilerleyin.");
      this.setState({ currentStep: 3 });
      window.scrollTo(0, 0);
      return;
    }

    if (!this.paymentStepRef.current.validate()) {
      return;
    }

    const paymentData = this.paymentStepRef.current.state;
    const comment = this.paymentStepRef.current.getComment();

    // Online Ödeme
    if (paymentData.selectedPaymentMethod === "online") {
      this.processOnlinePayment(comment);
    } else {
      // Havale/EFT
      this.processTransferPayment(comment);
    }
  };

  async processTransferPayment(comment) {
    const {
      isGuest,
      selectedAddressId,
      newAddressData,
      savedAddressData,
      savedInvoiceData,
    } = this.state;
    const prices = this.calculatePrices();

    this.setState({ isProcessing: true });

    // CRITICAL: Sync cart to backend before order creation

    const syncResult = await syncCartToBackend(
      this.props.cart.items,
      this.props.customer.customer
    );

    if (!syncResult.success) {
      console.error("[HavaleEFT] Sepet senkronizasyonu başarısız:", syncResult);
      alert("Sepet hazırlanırken bir hata oluştu. Lütfen tekrar deneyin.");
      this.setState({ isProcessing: false });
      return;
    }

    if (isGuest) {
      // Misafir kullanıcı
      if (!savedAddressData) {
        alert(
          "Adres bilgileri bulunamadı. Lütfen adım 1'e dönüp tekrar doldurun."
        );
        this.setState({ currentStep: 1, isProcessing: false });
        window.scrollTo(0, 0);
        return;
      }

      // CRITICAL FIX: Fatura tipi belirleme
      // - sameAsDelivery = true ise: Teslimat adresindeki corporateInvoice kullanılır
      // - sameAsDelivery = false ise: Fatura için ayrı corporateInvoice kontrol edilmeli
      //   Ancak şu an GuestAddressForm'da fatura için ayrı corporateInvoice checkbox'ı yok
      //   Bu yüzden invoice_company_name, invoice_tax_office, invoice_tax_number dolu ise kurumsal sayıyoruz
      let isCorporate = false;

      if (savedAddressData.sameAsDelivery) {
        // Teslimat adresiyle aynı ise, teslimat adresindeki corporateInvoice kullan
        isCorporate = savedAddressData.corporateInvoice || false;
      } else {
        // Fatura adresi farklı ise, fatura bilgilerinde kurumsal bilgi var mı kontrol et
        // Eğer invoice_company_name, invoice_tax_office, invoice_tax_number varsa kurumsal
        const hasInvoiceCompanyInfo =
          (savedAddressData.invoice_company_name &&
            savedAddressData.invoice_company_name.trim() !== "") ||
          (savedAddressData.invoice_tax_office &&
            savedAddressData.invoice_tax_office.trim() !== "") ||
          (savedAddressData.invoice_tax_number &&
            savedAddressData.invoice_tax_number.trim() !== "");

        isCorporate = hasInvoiceCompanyInfo;
      }

      const invoiceAddress = {
        invoice_first_name: savedAddressData.sameAsDelivery
          ? savedAddressData.first_name
          : savedAddressData.invoice_first_name,
        invoice_last_name: savedAddressData.sameAsDelivery
          ? savedAddressData.last_name
          : savedAddressData.invoice_last_name,
        invoice_type: isCorporate ? "corporate" : "individual",
        invoice_title: isCorporate
          ? savedAddressData.sameAsDelivery
            ? savedAddressData.invoice_company_name
            : savedAddressData.invoice_company_name
          : "", // Bireysel faturada invoice_title boş
        company_name: isCorporate
          ? savedAddressData.sameAsDelivery
            ? savedAddressData.invoice_company_name
            : savedAddressData.invoice_company_name
          : "",
        tax_office: isCorporate
          ? savedAddressData.invoice_tax_office || ""
          : "",
        tax_number: isCorporate
          ? savedAddressData.invoice_tax_number || ""
          : "",
        address: savedAddressData.sameAsDelivery
          ? savedAddressData.address
          : savedAddressData.invoice_address || savedAddressData.address,
        city: savedAddressData.sameAsDelivery
          ? savedAddressData.city
          : savedAddressData.invoice_city || savedAddressData.city,
        town: savedAddressData.sameAsDelivery
          ? savedAddressData.town
          : savedAddressData.invoice_town || savedAddressData.town,
        phone: savedAddressData.phone,
        email: savedAddressData.email,
      };

      this.props.createNewOrderForGuest({
        email: savedAddressData.email,
        first_name: savedAddressData.first_name,
        last_name: savedAddressData.last_name,
        city: savedAddressData.city,
        town: savedAddressData.town,
        phone: savedAddressData.phone,
        address: savedAddressData.address,
        invoice_address: invoiceAddress,
        comment: comment,
        payment: "Havale / EFT",
        products: this.props.cart.items,
        shipping_cost: prices.shippingCost,
        total_price: prices.totalPrice,
        discount: prices.cartTotalPrice - prices.cartTotalDiscountPrice,
        customer_discount: 0,
        setting: {
          shipping_cost: this.props.setting.setting.shipping_cost,
          free_shipping: this.props.setting.setting.free_shipping,
        },
      });
    } else {
      // Kayıtlı kullanıcı
      const { addresses } = this.props.customer;

      let selectedAddress;
      if (selectedAddressId === "new" && newAddressData) {
        selectedAddress = {
          first_name: newAddressData.first_name,
          last_name: newAddressData.last_name,
          city: newAddressData.city,
          town: newAddressData.town,
          phone: newAddressData.phone,
          address: newAddressData.address,
        };
      } else {
        selectedAddress =
          addresses &&
          addresses.find(function (addr) {
            return addr.id === selectedAddressId;
          });

        if (!selectedAddress) {
          alert("Lütfen bir adres seçiniz.");
          this.setState({ currentStep: 1, isProcessing: false });
          window.scrollTo(0, 0);
          return;
        }
      }

      if (!savedInvoiceData) {
        alert(
          "Fatura bilgileri bulunamadı. Lütfen adım 1'e dönüp fatura bilgilerini doldurun."
        );
        this.setState({ currentStep: 1, isProcessing: false });
        window.scrollTo(0, 0);
        return;
      }

      let invoiceAddress;
      if (savedInvoiceData.sameAsDelivery) {
        // Teslimat adresinden invoice_type bilgisini al
        var deliveryInvoiceType = "individual";
        var deliveryCompanyName = "";
        var deliveryTaxOffice = "";
        var deliveryTaxNumber = "";

        // Eğer teslimat adresi kurumsal faturaysa
        if (selectedAddressId === "new" && newAddressData) {
          deliveryInvoiceType = newAddressData.invoice_type || "individual";
          deliveryCompanyName = newAddressData.company_name || "";
          deliveryTaxOffice = newAddressData.tax_office || "";
          deliveryTaxNumber = newAddressData.tax_number || "";
        } else {
          var deliveryAddressFromDB = this.props.customer.addresses.find(
            function (addr) {
              return addr.id === selectedAddressId;
            }
          );
          if (deliveryAddressFromDB) {
            deliveryInvoiceType =
              deliveryAddressFromDB.invoice_type || "individual";
            deliveryCompanyName = deliveryAddressFromDB.company_name || "";
            deliveryTaxOffice = deliveryAddressFromDB.tax_office || "";
            deliveryTaxNumber = deliveryAddressFromDB.tax_number || "";
          }
        }

        var isDeliveryCorporate = deliveryInvoiceType === "corporate";

        invoiceAddress = {
          invoice_first_name: selectedAddress.first_name,
          invoice_last_name: selectedAddress.last_name,
          invoice_type: deliveryInvoiceType,
          invoice_title: isDeliveryCorporate ? deliveryCompanyName : "",
          company_name: deliveryCompanyName,
          tax_office: deliveryTaxOffice,
          tax_number: deliveryTaxNumber,
          address: selectedAddress.address,
          city: selectedAddress.city,
          town: selectedAddress.town,
          phone: selectedAddress.phone,
          email: this.props.customer.customer.email,
        };
      } else {
        // CRITICAL FIX: InvoiceAddressSelector.getData() now returns:
        // - invoice_first_name and invoice_last_name directly (no split needed)
        // - Both for new addresses (from form) and existing addresses (from DB)
        var invoiceFirstName =
          savedInvoiceData.invoice_first_name ||
          savedInvoiceData.first_name ||
          "";
        var invoiceLastName =
          savedInvoiceData.invoice_last_name ||
          savedInvoiceData.last_name ||
          "";

        // CRITICAL: sameAsDelivery is FALSE, so invoice info comes ONLY from savedInvoiceData
        // newAddressData is the DELIVERY address, not the invoice address
        var invoiceType = savedInvoiceData.invoice_type || "individual";
        var companyName = savedInvoiceData.company_name || "";
        var taxOffice = savedInvoiceData.tax_office || "";
        var taxNumber = savedInvoiceData.tax_number || "";

        var isCorporateInvoice = invoiceType === "corporate";

        invoiceAddress = {
          invoice_first_name: invoiceFirstName || "",
          invoice_last_name: invoiceLastName || "",
          invoice_type: invoiceType,
          invoice_title: isCorporateInvoice
            ? savedInvoiceData.invoice_title || companyName || ""
            : "", // Bireysel faturada invoice_title boş
          company_name: companyName,
          tax_office: taxOffice,
          tax_number: taxNumber,
          address: savedInvoiceData.address || "",
          city: savedInvoiceData.city || "",
          town: savedInvoiceData.town || "",
          phone: savedInvoiceData.phone || "",
          email: savedInvoiceData.email || this.props.customer.customer.email,
        };
      }

      const addressToSend =
        selectedAddressId === "new" && newAddressData
          ? newAddressData
          : selectedAddressId;

      this.props.createNewOrderForCustomer({
        address: addressToSend,
        invoice_address: invoiceAddress,
        comment: comment,
        payment: "Havale / EFT",
        products: this.props.cart.items,
        shipping_cost: prices.shippingCost,
        total_price: prices.totalPrice,
        discount: prices.cartTotalPrice - prices.cartTotalDiscountPrice,
        customer_discount: prices.customerDiscount,
        setting: {
          shipping_cost: this.props.setting.setting.shipping_cost,
          free_shipping: this.props.setting.setting.free_shipping,
        },
      });
    }
  }

  processOnlinePayment(comment) {
    const cardData = this.paymentStepRef.current.getCardData();
    if (!cardData) {
      alert("Lütfen kart bilgilerinizi kontrol ediniz.");
      return;
    }

    this.setState({ isProcessing: true });

    const {
      isGuest,
      selectedAddressId,
      newAddressData,
      savedAddressData,
      savedInvoiceData,
    } = this.state;
    const { addresses } = this.props.customer;
    const prices = this.calculatePrices();

    let paymentData;

    if (isGuest) {
      // Misafir - Online Ödeme
      // CRITICAL FIX: Fatura tipi belirleme
      // - sameAsDelivery = true ise: Teslimat adresindeki corporateInvoice kullanılır
      // - sameAsDelivery = false ise: Fatura için ayrı corporateInvoice kontrol edilmeli
      //   Ancak şu an GuestAddressForm'da fatura için ayrı corporateInvoice checkbox'ı yok
      //   Bu yüzden invoice_company_name, invoice_tax_office, invoice_tax_number dolu ise kurumsal sayıyoruz
      let isCorporate = false;

      if (savedAddressData.sameAsDelivery) {
        // Teslimat adresiyle aynı ise, teslimat adresindeki corporateInvoice kullan
        isCorporate = savedAddressData.corporateInvoice || false;
      } else {
        // Fatura adresi farklı ise, fatura bilgilerinde kurumsal bilgi var mı kontrol et
        // Eğer invoice_company_name, invoice_tax_office, invoice_tax_number varsa kurumsal
        const hasInvoiceCompanyInfo =
          (savedAddressData.invoice_company_name &&
            savedAddressData.invoice_company_name.trim() !== "") ||
          (savedAddressData.invoice_tax_office &&
            savedAddressData.invoice_tax_office.trim() !== "") ||
          (savedAddressData.invoice_tax_number &&
            savedAddressData.invoice_tax_number.trim() !== "");

        isCorporate = hasInvoiceCompanyInfo;
      }

      const invoiceAddress = {
        invoice_first_name: savedAddressData.sameAsDelivery
          ? savedAddressData.first_name
          : savedAddressData.invoice_first_name,
        invoice_last_name: savedAddressData.sameAsDelivery
          ? savedAddressData.last_name
          : savedAddressData.invoice_last_name,
        invoice_type: isCorporate ? "corporate" : "individual",
        invoice_title: isCorporate
          ? savedAddressData.sameAsDelivery
            ? savedAddressData.invoice_company_name
            : savedAddressData.invoice_company_name
          : "", // Bireysel faturada invoice_title boş
        company_name: isCorporate
          ? savedAddressData.sameAsDelivery
            ? savedAddressData.invoice_company_name
            : savedAddressData.invoice_company_name
          : "",
        tax_office: isCorporate
          ? savedAddressData.invoice_tax_office || ""
          : "",
        tax_number: isCorporate
          ? savedAddressData.invoice_tax_number || ""
          : "",
        address: savedAddressData.sameAsDelivery
          ? savedAddressData.address
          : savedAddressData.invoice_address || savedAddressData.address,
        city: savedAddressData.sameAsDelivery
          ? savedAddressData.city
          : savedAddressData.invoice_city || savedAddressData.city,
        town: savedAddressData.sameAsDelivery
          ? savedAddressData.town
          : savedAddressData.invoice_town || savedAddressData.town,
        phone: savedAddressData.phone,
        email: savedAddressData.email,
      };

      paymentData = {
        email: savedAddressData.email,
        first_name: savedAddressData.first_name,
        last_name: savedAddressData.last_name,
        phone: savedAddressData.phone,
        city: savedAddressData.city,
        town: savedAddressData.town,
        address: savedAddressData.address,
        invoice_address: invoiceAddress,
        products: this.props.cart.items.map(function (item) {
          return {
            product: item.id,
            product_name: item.name,
            quantity: item.quantity,
            price: item.sale_price
              ? parseFloat((item.sale_price * item.quantity).toFixed(2))
              : parseFloat((item.price * item.quantity).toFixed(2)),
          };
        }),
        comment: comment,
        shipping_cost: prices.shippingCost,
        total_price: prices.totalPrice,
        discount: prices.cartTotalPrice - prices.cartTotalDiscountPrice,
        customer_discount: 0,
        is_guest: true,
      };
    } else {
      // Kayıtlı kullanıcı

      let selectedAddress;
      if (selectedAddressId === "new" && newAddressData) {
        selectedAddress = {
          first_name: newAddressData.first_name,
          last_name: newAddressData.last_name,
          city: newAddressData.city,
          town: newAddressData.town,
          phone: newAddressData.phone,
          address: newAddressData.address,
        };
      } else {
        selectedAddress =
          addresses &&
          addresses.find(function (addr) {
            return addr.id === selectedAddressId;
          });
      }

      let invoiceAddress;
      if (savedInvoiceData.sameAsDelivery) {
        // Teslimat adresinden invoice_type bilgisini al
        var deliveryInvoiceType = "individual";
        var deliveryCompanyName = "";
        var deliveryTaxOffice = "";
        var deliveryTaxNumber = "";

        // Eğer teslimat adresi kurumsal faturaysa
        if (selectedAddressId === "new" && newAddressData) {
          deliveryInvoiceType = newAddressData.invoice_type || "individual";
          deliveryCompanyName = newAddressData.company_name || "";
          deliveryTaxOffice = newAddressData.tax_office || "";
          deliveryTaxNumber = newAddressData.tax_number || "";
        } else {
          var deliveryAddressFromDB = this.props.customer.addresses.find(
            function (addr) {
              return addr.id === selectedAddressId;
            }
          );
          if (deliveryAddressFromDB) {
            deliveryInvoiceType =
              deliveryAddressFromDB.invoice_type || "individual";
            deliveryCompanyName = deliveryAddressFromDB.company_name || "";
            deliveryTaxOffice = deliveryAddressFromDB.tax_office || "";
            deliveryTaxNumber = deliveryAddressFromDB.tax_number || "";
          }
        }

        var isDeliveryCorporate = deliveryInvoiceType === "corporate";

        invoiceAddress = {
          invoice_first_name: selectedAddress.first_name,
          invoice_last_name: selectedAddress.last_name,
          invoice_type: deliveryInvoiceType,
          invoice_title: isDeliveryCorporate ? deliveryCompanyName : "",
          company_name: deliveryCompanyName,
          tax_office: deliveryTaxOffice,
          tax_number: deliveryTaxNumber,
          address: selectedAddress.address,
          city: selectedAddress.city,
          town: selectedAddress.town,
          phone: selectedAddress.phone,
          email: this.props.customer.customer.email,
        };
      } else {
        // CRITICAL FIX: InvoiceAddressSelector.getData() now returns:
        // - invoice_first_name and invoice_last_name directly (no split needed)
        // - Both for new addresses (from form) and existing addresses (from DB)
        var invoiceFirstName =
          savedInvoiceData.invoice_first_name ||
          savedInvoiceData.first_name ||
          "";
        var invoiceLastName =
          savedInvoiceData.invoice_last_name ||
          savedInvoiceData.last_name ||
          "";

        // CRITICAL: sameAsDelivery is FALSE, so invoice info comes ONLY from savedInvoiceData
        // newAddressData is the DELIVERY address, not the invoice address
        var invoiceType = savedInvoiceData.invoice_type || "individual";
        var companyName = savedInvoiceData.company_name || "";
        var taxOffice = savedInvoiceData.tax_office || "";
        var taxNumber = savedInvoiceData.tax_number || "";

        var isCorporateInvoice = invoiceType === "corporate";

        invoiceAddress = {
          invoice_first_name: invoiceFirstName || "",
          invoice_last_name: invoiceLastName || "",
          invoice_type: invoiceType,
          invoice_title: isCorporateInvoice
            ? savedInvoiceData.invoice_title || companyName || ""
            : "", // Bireysel faturada invoice_title boş
          company_name: companyName,
          tax_office: taxOffice,
          tax_number: taxNumber,
          address: savedInvoiceData.address || "",
          city: savedInvoiceData.city || "",
          town: savedInvoiceData.town || "",
          phone: savedInvoiceData.phone || "",
          email: savedInvoiceData.email || this.props.customer.customer.email,
        };
      }

      paymentData = {
        is_guest: false,
        first_name: selectedAddress.first_name,
        last_name: selectedAddress.last_name,
        phone: selectedAddress.phone,
        city: selectedAddress.city,
        town: selectedAddress.town,
        address: selectedAddress.address,
        invoice_address: invoiceAddress,
        products: this.props.cart.items.map(function (item) {
          return {
            product: item.id,
            product_name: item.name,
            quantity: item.quantity,
            price: item.sale_price
              ? parseFloat((item.sale_price * item.quantity).toFixed(2))
              : parseFloat((item.price * item.quantity).toFixed(2)),
          };
        }),
        comment: comment,
        shipping_cost: prices.shippingCost,
        total_price: prices.totalPrice,
        discount: prices.cartTotalPrice - prices.cartTotalDiscountPrice,
        customer_discount: prices.customerDiscount,
        cc_owner: cardData.cc_owner,
        card_number: cardData.card_number,
        expiry_month: cardData.expiry_month,
        expiry_year: cardData.expiry_year,
        cvv: cardData.cvv,
        installment_count: cardData.installment_count || 0,
      };
    }

    this.props
      .initiatePaytrPayment(paymentData)
      .then(
        function (response) {
          if (response && response.success && response.payment_params) {
            // merchant_oid'yi localStorage'a kaydet (success page için)
            if (response.merchant_oid) {
              localStorage.setItem("paytr_merchant_oid", response.merchant_oid);
            }

            // PayTR'a yönlendirme için form oluştur ve submit et
            var form = document.createElement("form");
            form.method = "POST";
            form.action = "https://www.paytr.com/odeme";

            // PayTR parametrelerini ve kart bilgilerini birleştir
            var allParams = Object.assign({}, response.payment_params, {
              cc_owner: cardData.cc_owner,
              card_number: cardData.card_number,
              expiry_month: String(cardData.expiry_month).padStart(2, "0"),
              expiry_year: cardData.expiry_year,
              cvv: cardData.cvv,
              installment_count: cardData.installment_count || 0,
            });

            // Tüm parametreleri form'a ekle
            for (var key in allParams) {
              if (
                allParams.hasOwnProperty(key) &&
                allParams[key] !== null &&
                allParams[key] !== undefined
              ) {
                var input = document.createElement("input");
                input.type = "hidden";
                input.name = key;
                input.value = allParams[key];
                form.appendChild(input);
              }
            }

            document.body.appendChild(form);
            form.submit();
          } else {
            this.setState({ isProcessing: false });
            alert("Ödeme işlemi başlatılamadı. Lütfen tekrar deneyin.");
          }
        }.bind(this)
      )
      .catch(
        function (error) {
          this.setState({ isProcessing: false });
          console.error("PayTR Payment Error:", error);
          alert(
            "Ödeme işlemi başlatılamadı. Lütfen bilgilerinizi kontrol edip tekrar deneyin."
          );
        }.bind(this)
      );
  }

  // ==================== RENDER ====================

  render() {
    const {
      currentStep,
      completedSteps,
      isProcessing,
      isGuest,
      errors,
      selectedAddressId,
      savedAddressData,
    } = this.state;
    const { cart, setting, customer } = this.props;
    const prices = this.calculatePrices();

    // Address Summary
    let addressSummary = null;
    if (completedSteps.includes(1)) {
      if (isGuest && savedAddressData) {
        // ============ MİSAFİR KULLANICI ÖZETİ ============
        var isSameAddress = savedAddressData.sameAsDelivery;
        var isCorporateInvoice = savedAddressData.corporateInvoice;

        addressSummary = (
          <div
            style={{ marginTop: "5px", fontSize: "14px", lineHeight: "1.6" }}
          >
            {/* Teslimat Adresi */}
            <div style={{ marginBottom: "20px" }}>
              <div
                style={{
                  fontWeight: "700",
                  color: "#84b817",
                  fontSize: "12px",
                  textTransform: "uppercase",
                }}
              >
                TESLİMAT ADRESİ
              </div>
              <div
                style={{
                  color: "#1a1a1a",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                {savedAddressData.first_name} {savedAddressData.last_name}
              </div>
              <div
                style={{ color: "#4a4a4a", fontSize: "14px", marginTop: "2px" }}
              >
                {savedAddressData.phone}
              </div>
              <div
                style={{ color: "#4a4a4a", fontSize: "14px", marginTop: "2px" }}
              >
                {savedAddressData.address} {savedAddressData.town},{" "}
                {savedAddressData.city}
              </div>
            </div>

            {/* Fatura Adresi */}
            <div>
              <div
                style={{
                  fontWeight: "700",
                  color: "#84b817",
                  fontSize: "12px",
                  textTransform: "uppercase",
                }}
              >
                FATURA ADRESİ{" "}
                <span
                  style={{
                    color: "#6c757d",
                    fontStyle: "italic",
                  }}
                >
                  {isCorporateInvoice && "(Kurumsal Fatura)"}
                </span>
              </div>
              {isSameAddress && !isCorporateInvoice ? (
                <div
                  style={{
                    color: "#6c757d",
                    fontStyle: "italic",
                    fontSize: "14px",
                  }}
                >
                  Teslimat adresiyle aynı
                </div>
              ) : isSameAddress && isCorporateInvoice ? (
                <React.Fragment>
                  <div
                    style={{
                      color: "#1a1a1a",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    {savedAddressData.invoice_company_name}
                  </div>
                  <div
                    style={{
                      color: "#4a4a4a",
                      fontSize: "13px",
                      marginTop: "2px",
                    }}
                  >
                    Vergi No: {savedAddressData.invoice_tax_number}
                  </div>
                  <div
                    style={{
                      color: "#4a4a4a",
                      fontSize: "13px",
                      marginTop: "2px",
                    }}
                  >
                    Vergi Dairesi: {savedAddressData.invoice_tax_office}
                  </div>
                  <div
                    style={{
                      color: "#6c757d",
                      fontStyle: "italic",
                      fontSize: "14px",
                      marginTop: "4px",
                    }}
                  >
                    Teslimat adresiyle aynı
                  </div>
                </React.Fragment>
              ) : !isSameAddress && !isCorporateInvoice ? (
                <React.Fragment>
                  <div
                    style={{
                      color: "#1a1a1a",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    {savedAddressData.invoice_first_name ||
                      savedAddressData.first_name}{" "}
                    {savedAddressData.invoice_last_name ||
                      savedAddressData.last_name}
                  </div>
                  <div
                    style={{
                      color: "#4a4a4a",
                      fontSize: "14px",
                      marginTop: "2px",
                    }}
                  >
                    {savedAddressData.invoice_address ||
                      savedAddressData.address}{" "}
                    {savedAddressData.invoice_town || savedAddressData.town},{" "}
                    {savedAddressData.invoice_city || savedAddressData.city}
                  </div>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <div
                    style={{
                      color: "#1a1a1a",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    {savedAddressData.invoice_company_name}
                  </div>
                  <div
                    style={{
                      color: "#4a4a4a",
                      fontSize: "13px",
                      marginTop: "2px",
                    }}
                  >
                    Vergi No: {savedAddressData.invoice_tax_number}
                  </div>
                  <div
                    style={{
                      color: "#4a4a4a",
                      fontSize: "13px",
                      marginTop: "2px",
                    }}
                  >
                    Vergi Dairesi: {savedAddressData.invoice_tax_office}
                  </div>
                  <div
                    style={{
                      color: "#4a4a4a",
                      fontSize: "14px",
                      marginTop: "2px",
                    }}
                  >
                    {savedAddressData.invoice_address ||
                      savedAddressData.address}{" "}
                    {savedAddressData.invoice_town || savedAddressData.town},{" "}
                    {savedAddressData.invoice_city || savedAddressData.city}
                  </div>
                </React.Fragment>
              )}
            </div>
          </div>
        );
      } else if (!isGuest) {
        // ============ KAYITLI KULLANICI ÖZETİ ============
        const { addresses } = customer;
        var selectedAddress =
          addresses &&
          addresses.find(function (addr) {
            return addr.id === selectedAddressId;
          });

        if (!selectedAddress && this.state.newAddressData) {
          selectedAddress = this.state.newAddressData;
        }

        if (selectedAddress) {
          var invoiceData = this.state.savedInvoiceData;
          var isSameAsDelivery = invoiceData && invoiceData.sameAsDelivery;

          var isDeliveryCorporate =
            selectedAddress.invoice_type === "corporate" &&
            selectedAddress.company_name;

          var isInvoiceCorporate = false;
          if (!isSameAsDelivery && invoiceData) {
            isInvoiceCorporate = invoiceData.invoice_type === "corporate";
          } else if (isSameAsDelivery) {
            isInvoiceCorporate = isDeliveryCorporate;
          }

          addressSummary = (
            <div
              style={{ marginTop: "5px", fontSize: "14px", lineHeight: "1.6" }}
            >
              {/* Teslimat Adresi */}
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    fontWeight: "700",
                    color: "#84b817",
                    fontSize: "12px",
                    textTransform: "uppercase",
                  }}
                >
                  TESLİMAT ADRESİ
                </div>
                <div
                  style={{
                    color: "#1a1a1a",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  {selectedAddress.first_name} {selectedAddress.last_name}
                </div>
                <div
                  style={{
                    color: "#4a4a4a",
                    fontSize: "14px",
                    marginTop: "2px",
                  }}
                >
                  {selectedAddress.phone}
                </div>
                <div
                  style={{
                    color: "#4a4a4a",
                    fontSize: "14px",
                    marginTop: "2px",
                  }}
                >
                  {selectedAddress.address} {selectedAddress.town},{" "}
                  {selectedAddress.city}
                </div>
              </div>

              {/* Fatura Adresi */}
              <div>
                <div
                  style={{
                    fontWeight: "700",
                    color: "#84b817",
                    fontSize: "12px",
                    textTransform: "uppercase",
                  }}
                >
                  FATURA ADRESİ{" "}
                  <span
                    style={{
                      color: "#6c757d",
                      fontStyle: "italic",
                    }}
                  >
                    {isInvoiceCorporate && "(Kurumsal Fatura)"}
                  </span>
                </div>
                {isSameAsDelivery && !isDeliveryCorporate ? (
                  <div
                    style={{
                      color: "#6c757d",
                      fontStyle: "italic",
                      fontSize: "14px",
                    }}
                  >
                    Teslimat adresiyle aynı
                  </div>
                ) : isSameAsDelivery && isDeliveryCorporate ? (
                  <React.Fragment>
                    <div
                      style={{
                        color: "#1a1a1a",
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      {selectedAddress.company_name}
                    </div>
                    <div
                      style={{
                        color: "#4a4a4a",
                        fontSize: "13px",
                        marginTop: "2px",
                      }}
                    >
                      Vergi No: {selectedAddress.tax_number}
                    </div>
                    <div
                      style={{
                        color: "#4a4a4a",
                        fontSize: "13px",
                        marginTop: "2px",
                      }}
                    >
                      Vergi Dairesi: {selectedAddress.tax_office}
                    </div>
                    <div
                      style={{
                        color: "#6c757d",
                        fontStyle: "italic",
                        fontSize: "14px",
                        marginTop: "4px",
                      }}
                    >
                      Teslimat adresiyle aynı
                    </div>
                  </React.Fragment>
                ) : !isSameAsDelivery && invoiceData && !isInvoiceCorporate ? (
                  <React.Fragment>
                    <div
                      style={{
                        color: "#1a1a1a",
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      {(invoiceData.invoice_first_name ||
                        invoiceData.first_name ||
                        "") +
                        " " +
                        (invoiceData.invoice_last_name ||
                          invoiceData.last_name ||
                          "")}
                    </div>
                    <div
                      style={{
                        color: "#4a4a4a",
                        fontSize: "14px",
                        marginTop: "2px",
                      }}
                    >
                      {invoiceData.address} {invoiceData.town},{" "}
                      {invoiceData.city}
                    </div>
                  </React.Fragment>
                ) : !isSameAsDelivery && invoiceData && isInvoiceCorporate ? (
                  <React.Fragment>
                    <div
                      style={{
                        color: "#1a1a1a",
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      {invoiceData.company_name || invoiceData.invoice_title}
                    </div>
                    <div
                      style={{
                        color: "#4a4a4a",
                        fontSize: "13px",
                        marginTop: "2px",
                      }}
                    >
                      Vergi No: {invoiceData.tax_number}
                    </div>
                    <div
                      style={{
                        color: "#4a4a4a",
                        fontSize: "13px",
                        marginTop: "2px",
                      }}
                    >
                      Vergi Dairesi: {invoiceData.tax_office}
                    </div>
                    <div
                      style={{
                        color: "#4a4a4a",
                        fontSize: "14px",
                        marginTop: "2px",
                      }}
                    >
                      {invoiceData.address} {invoiceData.town},{" "}
                      {invoiceData.city}
                    </div>
                  </React.Fragment>
                ) : (
                  <div
                    style={{
                      color: "#6c757d",
                      fontStyle: "italic",
                      fontSize: "14px",
                    }}
                  >
                    Fatura adresi seçilmedi
                  </div>
                )}
              </div>
            </div>
          );
        }
      }
    }

    // Left Panel
    const leftPanel = (
      <form onSubmit={this.onSubmit}>
        {/* Sipariş Hata Mesajları */}
        {errors && errors.length > 0 && (
          <div
            style={{
              backgroundColor: "#fee",
              color: "#721c24",
              border: "2px solid #dc3545",
              borderRadius: "8px",
              padding: "16px 20px",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "flex-start", flex: 1 }}
              >
                <i
                  className="fa fa-exclamation-circle"
                  style={{
                    marginRight: "12px",
                    fontSize: "24px",
                    marginTop: "2px",
                    color: "#dc3545",
                  }}
                ></i>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: "18px",
                      marginBottom: "8px",
                      color: "#dc3545",
                    }}
                  >
                    Sipariş Oluşturulamadı
                  </div>
                  {errors.map(function (error, index) {
                    return (
                      <div
                        key={index}
                        style={{
                          fontSize: "14px",
                          lineHeight: "1.5",
                          margin: "4px 0",
                        }}
                      >
                        {error}
                      </div>
                    );
                  })}
                  <div
                    style={{
                      fontSize: "13px",
                      marginTop: "12px",
                      padding: "8px 12px",
                      backgroundColor: "#fff3cd",
                      borderLeft: "3px solid #ffc107",
                      borderRadius: "4px",
                      color: "#856404",
                      lineHeight: "1.5",
                    }}
                  >
                    <i
                      className="fa fa-info-circle"
                      style={{ marginRight: "8px" }}
                    ></i>
                    Lütfen bilgilerinizi kontrol edip tekrar deneyin. Sorun
                    devam ederse müşteri hizmetleriyle iletişime geçin.
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={function () {
                  this.setState({ errors: [] });
                }.bind(this)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#dc3545",
                  fontSize: "20px",
                  cursor: "pointer",
                  padding: "4px 8px",
                  marginLeft: "15px",
                  lineHeight: "1",
                }}
                title="Kapat"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
          </div>
        )}

        {/* Ödeme Hatası Mesajı */}
        {this.state.paymentError && (
          <div
            style={{
              backgroundColor: "#f8d7da",
              color: "#721c24",
              border: "1px solid #f5c6cb",
              borderRadius: "4px",
              padding: "15px 20px",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "flex-start", flex: 1 }}
              >
                <i
                  className="fas fa-exclamation-triangle"
                  style={{
                    marginRight: "12px",
                    fontSize: "20px",
                    marginTop: "2px",
                  }}
                ></i>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: "15px",
                      marginBottom: "6px",
                    }}
                  >
                    Ödeme İşlemi Başarısız
                  </div>
                  <div style={{ fontSize: "14px", lineHeight: "1.5" }}>
                    {this.state.paymentError}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      marginTop: "8px",
                      color: "#856404",
                    }}
                  >
                    Lütfen kart bilgilerinizi kontrol edip tekrar deneyiniz veya
                    farklı bir ödeme yöntemi seçiniz.
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={function () {
                  this.setState({ paymentError: null });
                }.bind(this)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#721c24",
                  fontSize: "24px",
                  cursor: "pointer",
                  padding: "0",
                  marginLeft: "15px",
                  lineHeight: "1",
                  fontWeight: "300",
                }}
                title="Kapat"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* ADIM 1: ADRES */}
        <CheckoutStepCard
          stepNumber={1}
          title="Adres"
          isActive={currentStep === 1}
          isCompleted={completedSteps.includes(1)}
          summaryContent={addressSummary}
          onEdit={function () {
            this.setState({ currentStep: 1 });
          }.bind(this)}
        >
          <div style={styles.stepContainer}>
            {isGuest ? (
              // Misafir: GuestAddressForm
              <GuestAddressForm
                ref={this.addressFormRef}
                initialData={savedAddressData}
              />
            ) : (
              // Kayıtlı kullanıcı: AddressList + InvoiceAddressSelector
              <React.Fragment>
                <AddressList
                  addresses={customer.addresses || []}
                  selectedAddressId={selectedAddressId}
                  editingAddressId={this.state.editingAddressId}
                  isSavingAddress={this.state.isSavingAddress}
                  onAddressSelect={this.handleAddressSelect}
                  onNewAddress={this.handleNewAddress}
                  onEditAddress={this.handleEditAddress}
                  onCancelEdit={this.handleCancelEdit}
                  onSaveEdit={this.handleSaveEdit}
                  newAddressForm={
                    <RegisteredAddressForm
                      key="delivery-new-address"
                      formPrefix="delivery_"
                      ref={this.deliveryAddressFormRef}
                      initialData={this.getNewAddressData()}
                      onChange={this.handleDeliveryFormChange}
                    />
                  }
                  editAddressForm={
                    this.state.editingAddressId ? (
                      <RegisteredAddressForm
                        key={"delivery-edit-" + this.state.editingAddressId}
                        formPrefix="delivery_"
                        ref={this.deliveryAddressFormRef}
                        initialData={this.getEditingAddressData()}
                        isEditMode={true}
                        onChange={this.handleDeliveryFormChange}
                      />
                    ) : null
                  }
                />

                <InvoiceAddressSelector
                  ref={this.invoiceAddressSelectorRef}
                  isRegistered={true}
                  addresses={customer.addresses}
                  sameAsDelivery={this.state.invoiceSameAsDelivery}
                  onSameAsDeliveryChange={
                    this.handleInvoiceSameAsDeliveryChange
                  }
                  externalState={this.state.invoiceAddressState}
                  onStateChange={this.handleInvoiceAddressStateChange}
                  onSaveAddress={this.handleSaveInvoiceAddress}
                  isSavingAddress={this.state.isSavingAddress}
                />
              </React.Fragment>
            )}

            <div style={styles.stepActions}>
              <button
                type="button"
                onClick={this.handleStepNext}
                style={styles.nextButton}
              >
                Kargo ile Devam Et
              </button>
            </div>
          </div>
        </CheckoutStepCard>

        {/* ADIM 2: KARGO */}
        <CheckoutStepCard
          stepNumber={2}
          title="Kargo"
          isActive={currentStep === 2}
          isCompleted={completedSteps.includes(2)}
          summaryContent={
            completedSteps.includes(2) ? (
              <div>
                <span style={styles.shippingMethod}>HepsiJet</span>
                {prices.shippingCost > 0 ? (
                  <span style={styles.shippingCost}>
                    {prices.shippingCost.toFixed(2)} ₺
                  </span>
                ) : (
                  <span style={styles.freeShipping}>Ücretsiz</span>
                )}
              </div>
            ) : null
          }
          onEdit={function () {
            this.setState({ currentStep: 2 });
          }.bind(this)}
        >
          <div style={styles.stepContainer}>
            <div style={styles.shippingOption}>
              <div style={styles.shippingOptionHeader}>
                <div style={styles.shippingRadio}>
                  <input
                    type="radio"
                    checked={true}
                    readOnly
                    style={styles.radioInput}
                  />
                  <label style={styles.shippingLabel}>HepsiJet</label>
                </div>
                <div style={styles.shippingPrice}>
                  {prices.shippingCost > 0 ? (
                    <span>{prices.shippingCost.toFixed(2)} ₺</span>
                  ) : (
                    <span style={styles.freeShippingBadge}>Ücretsiz</span>
                  )}
                </div>
              </div>
            </div>

            <div style={styles.stepActions}>
              <button
                type="button"
                onClick={this.handleStepNext}
                style={styles.nextButton}
              >
                Ödeme ile Devam Et
              </button>
            </div>
          </div>
        </CheckoutStepCard>

        {/* ADIM 3: ÖDEME */}
        <CheckoutStepCard
          stepNumber={3}
          title="Ödeme"
          isActive={currentStep === 3}
          isCompleted={completedSteps.includes(3)}
        >
          <div style={styles.stepContainer}>
            <PaymentStep
              ref={this.paymentStepRef}
              payments={
                setting && setting.setting ? setting.setting.payments : []
              }
            />

            <div style={styles.stepActions}>
              <button
                type="submit"
                disabled={
                  isProcessing || !cart.items || cart.items.length === 0
                }
                style={{
                  ...styles.submitButton,
                  ...(isProcessing || !cart.items || cart.items.length === 0
                    ? styles.disabledButton
                    : {}),
                }}
              >
                {isProcessing ? (
                  <React.Fragment>
                    <i
                      className="fa fa-spinner fa-spin"
                      style={styles.buttonIconLeft}
                    ></i>
                    İşleniyor...
                  </React.Fragment>
                ) : (
                  <React.Fragment>Siparişi Tamamla</React.Fragment>
                )}
              </button>

              {isGuest && (
                <div style={styles.securityMessage}>
                  <i className="fa fa-lock" style={styles.securityIcon}></i>
                  Ödemeler güvenli ve şifrelidir
                </div>
              )}
            </div>
          </div>
        </CheckoutStepCard>
      </form>
    );

    // Right Panel
    const rightPanel = <OrderSummaryWidget />;

    return (
      <CheckoutMinimalLayout
        leftPanel={leftPanel}
        rightPanel={rightPanel}
        customer={isGuest ? null : customer.customer}
        isGuest={isGuest}
      />
    );
  }
}

// ==================== STYLES ====================

const styles = {
  stepContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "32px",
  },
  emptyState: {
    textAlign: "center",
    padding: "48px 24px",
    backgroundColor: "#f8f9fa",
    borderRadius: "12px",
    border: "2px dashed #dee2e6",
  },
  emptyStateHeader: {
    textAlign: "center",
    padding: "24px 24px 16px",
    backgroundColor: "#f8f9fa",
    borderRadius: "12px 12px 0 0",
    marginBottom: "16px",
  },
  emptyIcon: {
    fontSize: "48px",
    color: "#84b817",
    marginBottom: "16px",
  },
  emptyText: {
    fontSize: "16px",
    color: "#6c757d",
    marginBottom: "24px",
  },
  newAddressButton: {
    backgroundColor: "#84b817",
    color: "#fff",
    border: "none",
    padding: "12px 32px",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(132, 184, 23, 0.2)",
  },
  stepActions: {
    marginTop: "0",
    display: "flex",
    flexWrap: "wrap",
    gap: "16px",
  },
  nextButton: {
    flex: "1 1 240px",
    backgroundColor: "#84b817",
    color: "#ffffff",
    border: "none",
    padding: "16px 20px",
    borderRadius: "14px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 14px 28px rgba(132, 184, 23, 0.28)",
  },
  submitButton: {
    flex: "1 1 100%",
    backgroundColor: "#84b817",
    color: "#ffffff",
    border: "none",
    padding: "18px 24px",
    borderRadius: "14px",
    fontSize: "18px",
    fontWeight: "700",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 18px 32px rgba(132, 184, 23, 0.3)",
  },
  disabledButton: {
    backgroundColor: "#cfd7e2",
    cursor: "not-allowed",
    opacity: 0.7,
    boxShadow: "none",
  },
  buttonIconLeft: {
    marginRight: "12px",
    fontSize: "18px",
  },
  shippingOption: {
    padding: "16px",
    border: "2px solid #84b817",
    borderRadius: "12px",
    backgroundColor: "#f8f7ff",
    marginBottom: "16px",
  },
  shippingOptionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  shippingRadio: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  radioInput: {
    width: "20px",
    height: "20px",
    accentColor: "#5f8d00",
  },
  shippingLabel: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#212529",
    margin: 0,
  },
  shippingPrice: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#212529",
  },
  shippingMethod: {
    fontSize: "14px",
    color: "#212529",
    fontWeight: "500",
  },
  shippingCost: {
    fontSize: "14px",
    color: "#212529",
    marginLeft: "8px",
  },
  freeShipping: {
    fontSize: "14px",
    color: "#84b817",
    fontWeight: "600",
    marginLeft: "8px",
  },
  freeShippingBadge: {
    color: "#84b817",
    fontWeight: "600",
  },
  securityMessage: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    marginTop: "16px",
    fontSize: "14px",
    color: "#6b7280",
    fontWeight: "500",
    width: "100%",
    textAlign: "center",
  },
  securityIcon: {
    fontSize: "16px",
    color: "#84b817",
  },
};

// ==================== REDUX ====================

const mapStateToProps = function ({ cart, customer, order, page, setting }) {
  return { cart, customer, order, page, setting };
};

const mapDispatchToProps = {
  createNewOrderForCustomer,
  createNewOrderForGuest,
  initiatePaytrPayment,
  customerGetAddress,
  addNewAddress,
  updateAddress,
};

export default connect(mapStateToProps, mapDispatchToProps)(UnifiedCheckout);



// WEBPACK FOOTER //
// ./src/routers/Checkout/unified.js